import { _ } from "../polyglot/polyglot.js";

/**
 * This service is responsible for handling CRUD operations on an
 * owner's own key figures.
 *
 * @param {Country|Agency} owner - The owner of key figures.
 * @param {KeyFigureService} service - To manage the key figure
 * repository and cache.
 */
export default class OwnKeyFigureSettingsService {
	constructor({ owner, service }) {
		this._owner = owner;
		this._service = service;
	}

	setOwner(owner) {
		this._owner = owner;
	}

	getAllFromCache() {
		return this._service.getAllFromCache();
	}

	getAllFromCacheAsMap() {
		return this._service.getAllFromCacheAsMap();
	}

	findAll(callback = () => {}) {
		return this.getAllFromCache().then((allKeyFigures) => {
			const ownKeyFigures = allKeyFigures.filter((keyFigure) =>
				keyFigure.belongsTo(this._owner),
			);

			callback(ownKeyFigures);
			return ownKeyFigures;
		});
	}

	getDownloadUrlFor(model) {
		const keyFigureId = model.getId();
		const url = this._service.getDownloadUrl(keyFigureId);

		return `/${url}`;
	}

	save({ modelData, callback = (arg) => arg }) {
		const keyFigure = this._modelDataToSerializableFakeKeyFigure(modelData);

		return this._service.update(keyFigure).then(callback);
	}

	create({ modelData, callback = (arg) => arg }) {
		const keyFigure = this._modelDataToSerializableFakeKeyFigure(modelData);

		return this._service
			.create(keyFigure, { successMessage: _("New key figure saved") })
			.then(callback);
	}

	delete({ modelId, callback = (arg) => arg }) {
		return this._service
			.get(modelId)
			.then((keyFigure) => this._service.remove(keyFigure))
			.then(callback);
	}

	canRestore(model) {
		return model.canBeRestoredFor(this._owner);
	}

	canDelete(model) {
		return model.canBeDeletedBy(this._owner);
	}

	canDownload(model) {
		return model.canBeDownloadedBy(this._owner);
	}

	canUpload(model) {
		return model.canBeUploadedBy(this._owner);
	}

	/**
	 * Convert `modelData`, a JS object ready to be materialized,
	 * into an object that `this._service` can send accross the
	 * network.
	 */
	_modelDataToSerializableFakeKeyFigure(modelData) {
		return {
			getId: () => modelData.id,
			serialize: () => modelData,
		};
	}
}
